import React, { useState, useEffect } from 'react';
import { Assureur } from '../../types';

interface AssureurFormProps {
  assureur?: Assureur | null;
  onSubmit: (data: Omit<Assureur, 'id' | 'created_at'>) => void;
  onCancel: () => void;
  loading: boolean;
}

const AssureurForm: React.FC<AssureurFormProps> = ({ 
  assureur, 
  onSubmit, 
  onCancel, 
  loading 
}) => {
  const [formData, setFormData] = useState<Omit<Assureur, 'id' | 'created_at'>>({
    nom: '',
    contact: '',
    telephone: '',
    email: '',
    adresse: '',
    taux_couverture: 80.0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (assureur) {
      setFormData({
        nom: assureur.nom,
        contact: assureur.contact || '',
        telephone: assureur.telephone || '',
        email: assureur.email || '',
        adresse: assureur.adresse || '',
        taux_couverture: assureur.taux_couverture || 80.0,
      });
    }
  }, [assureur]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Gérer les valeurs numériques pour taux_couverture
    const processedValue = name === 'taux_couverture' ? parseFloat(value) || 0 : value;
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (formData.taux_couverture < 0 || formData.taux_couverture > 100) {
      newErrors.taux_couverture = 'Le taux de couverture doit être entre 0 et 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {assureur ? 'Modifier l\'assureur' : 'Nouvel assureur'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="nom" className="block text-sm font-medium text-gray-700">
                Nom *
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.nom ? 'border-red-300' : ''
                }`}
                disabled={loading}
                placeholder="Nom de l'assureur"
              />
              {errors.nom && (
                <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
              )}
            </div>

            <div>
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700">
                Personne de contact
              </label>
              <input
                type="text"
                id="contact"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                disabled={loading}
                placeholder="Nom du contact"
              />
            </div>

            <div>
              <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">
                Téléphone
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                disabled={loading}
                placeholder="Numéro de téléphone"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.email ? 'border-red-300' : ''
                }`}
                disabled={loading}
                placeholder="Adresse email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="adresse" className="block text-sm font-medium text-gray-700">
                Adresse
              </label>
              <textarea
                id="adresse"
                name="adresse"
                rows={3}
                value={formData.adresse}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                disabled={loading}
                placeholder="Adresse complète"
              />
            </div>

            <div>
              <label htmlFor="taux_couverture" className="block text-sm font-medium text-gray-700">
                Taux de couverture (%) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="taux_couverture"
                  name="taux_couverture"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.taux_couverture}
                  onChange={handleChange}
                  className={`mt-1 block w-full pr-8 rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                    errors.taux_couverture ? 'border-red-300' : ''
                  }`}
                  disabled={loading}
                  placeholder="80.00"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
              </div>
              {errors.taux_couverture && (
                <p className="mt-1 text-sm text-red-600">{errors.taux_couverture}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Pourcentage de prise en charge par défaut pour cet assureur
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : assureur ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssureurForm;